import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { EllipsisIcon, CopyIcon, WarningIcon, QuestionMarkIcon } from "../../../components/Svg";
import { ModalTypes, Urls } from '../../../constants';
import UIState from '../../../store/UIState';
import Ai, { selectAllGpuClusters, selectAllVMs } from '../../../store/models/Ai';
import { getLinkByDeployment, checkActiveSSH, getVmResourcesString, getStatus, getStatusString } from '../../../utils';
import { getClusterStatusSum, checkActivePod, age, checkDeploymentsReady } from '../../../utils';
import classNames from 'classnames';
import { Button } from '../../../components/Button';
import Loader from '../../../components/Loader';
import { selectCurrentUserId } from '../../../store/models/User';
import { selectCurrentProjectId } from '../../../store/models/Project';
import _ from "lodash";
import { FaCheckCircle, FaEdit, FaTimesCircle } from "react-icons/fa";
import { selectAppStarted } from '../../../store/AppState';
import ApiKeyNote from '../../../components/ApiKeyNote';
import Tooltip from "../../../components/Tooltip";
import { mergeUrl } from "../../../utils/url";
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";

function GpuClusterPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [curDir, setCurDir] = useState('');
  const [curType, setCurType] = useState('');
  const appStarted = useSelector(state => selectAppStarted(state));
  const userId = useSelector(state => selectCurrentUserId(state));
  const projectId = useSelector(state => selectCurrentProjectId(state));
  const gpuClusters = useSelector(state => selectAllGpuClusters(state, projectId));
  const vms = useSelector(state => selectAllVMs(state));
  const hasNoGpuClusters = (!_.isNil(gpuClusters) && _.isEmpty(gpuClusters));
  const [activePopup, setActivePopup] = useState(-1);
  const history = useHistory();
  const [deploymentMap, setDeploymentMap] = useState({});
  let timeoutId;

  async function fetchData() {
    try {
      setError()
      const gpuClusters = await dispatch(Ai.actions.fetchGpuClusters(projectId));
      setLoading(false);
      if (!checkDeploymentsReady(gpuClusters)) {
        timeoutId = setTimeout(fetchData, 1000 * 5)
      }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!appStarted || _.isNil(userId)) return;
    setLoading(true);
    fetchData();
    return () => { clearTimeout(timeoutId) };
  }, [projectId, Object.keys(gpuClusters).length]);


  const handleOpenModel = () => {
    dispatch(UIState.actions.showModal(ModalTypes.MANAGE_GPU_CLUSTER))
  }

  const handlePopup = (e, i) => {
    e.stopPropagation();
    e.preventDefault();
    if (i === -1) {
      setActivePopup(i)
      return;
    }
    setActivePopup(v => {
      if (v === i) return -1;
      return i;
    })
  }

  const handleDelete = (cluster) => {
    const onConfirm = async () => {
      try {
        if (cluster.Suffix) {
          await dispatch(Ai.actions.deleteGpuCluster(projectId, cluster.Suffix));
        } else {
          await dispatch(Ai.actions.deleteGpuClusterBase(projectId, cluster.ID));
        }
        await dispatch(Ai.actions.fetchGpuClusters(projectId));
        toast.success(`The deployment has been deleted.`)
      } catch (e) {
        toast.error(`Failed to delete the deployment. Error: ${e.message}`)
      }
    }
    dispatch(UIState.actions.showModal(ModalTypes.DELETE, { title: cluster.Name, onConfirm }));
    setActivePopup(-1);
  }

  const handleReconfigure = (gpuNode, isStopped) => {
    if (!isStopped) return;
    let url = mergeUrl(Urls.AI_GPU_NODE_UPDATE, { projectId, id: gpuNode.ID })
    history.push(url + '?t=list');
    // dispatch(UIState.actions.showModal(ModalTypes.MANAGE_GPU_CLUSTER, { deployment: gpuNode }));
    setActivePopup(-1);
  }

  const handleStart = (gpuNode) => {
    const onConfirm = async () => {
      try {
        await dispatch(Ai.actions.startDeployment(projectId, gpuNode.ID));
        await fetchData();
        toast.success(`The deployment has been started.`)
      } catch (e) {
        toast.error(`Failed to start the deployment. Error: ${e.message}`)
      }
    }
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Start deployment",
      message: "Are you sure you want to start the deployment?",
      onConfirm: onConfirm,
      confirmLabel: "Yes",
      cancelLabel: "No",
      size: 's'
    }));
    setActivePopup(-1);
  }

  const handleStop = (gpuNode) => {
    const onConfirm = async () => {
      try {
        await dispatch(Ai.actions.stopDeployment(projectId, gpuNode.Shard, gpuNode.Suffix));
        await dispatch(Ai.actions.fetchGpuNodes(projectId));
        toast.success(`The deployment has been stopped.`)
      } catch (e) {
        toast.error(`Failed to stop the deployment. Error: ${e.message}`)
      }
    }
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: <div style={{ display: 'flex' }}>
        <div style={{ marginRight: 10 }}><WarningIcon /></div>
        Stop deployment
      </div>,
      message: <>
        <div style={{ padding: 10 }}>
          WARNING: Data stored in ephemeral storage will be lost when a GPU node is stopped. To ensure data preservation, we recommend attaching a persistent volume to your GPU node during its creation. Persistent volumes safely retain data even when the node is stopped.
        </div>
        <div style={{ marginBottom: -20, marginTop: 10 }}>
          Do you still want to stop the deployment?
        </div>
      </>,
      onConfirm: onConfirm,
      confirmLabel: "Yes, stop deployment",
      cancelLabel: "No, keep it running",
      size: 's'
    }));
    setActivePopup(-1);
  }

  const handleShowSSH = async (gpuCluster) => {
    const suffix = _.get(gpuCluster, 'Suffix');
    let deployments;
    if (!deploymentMap[suffix]) {
      deployments = await dispatch(Ai.actions.fetchGpuClusterDeployments(projectId, suffix));
      setDeploymentMap(map => ({
        ...map,
        [suffix]: deployments
      }))
    } else {
      deployments = deploymentMap[suffix]
    }
    const message = (deployments.map((deployment, i) => {
      let portKey = _.get(deployment, 'AdditionalPorts.0');
      let mapPort = _.get(deployment, `PortMappings.${portKey}`);
      const msgString = `ssh -i ~/.ssh/id_rsa -p ${mapPort} root@${_.get(deployment, 'NodePublicIP')}`
      return <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '5px' }}>
        {<>
          node{i + 1}: {msgString}
          <div className='AiServicePage__copy-button-wrap'
            onClick={() => {
              navigator.clipboard.writeText(msgString);
              toast.success("Copied to clipboard")
            }} >
            <CopyIcon />
          </div>
        </>}
      </div>
      // return <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '5px' }}>
      //   {checkActiveSSH(deployment) ? <>
      //     node{i + 1}: {msgString}
      //     <div className='AiServicePage__copy-button-wrap'
      //       onClick={() => {
      //         navigator.clipboard.writeText(msgString);
      //         toast.success("Copied to clipboard")
      //       }} >
      //       <CopyIcon />
      //     </div></> : `node${i + 1}: SSH is currently unavailable.`}
      // </div>
    })
    );
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "SSH to the nodes",
      message: message
    }));
  }

  const handleShowEndpoint = async (gpuCluster) => {
    const suffix = _.get(gpuCluster, 'Suffix');
    let deployments;
    if (!deploymentMap[suffix]) {
      deployments = await dispatch(Ai.actions.fetchGpuClusterDeployments(projectId, suffix));
      setDeploymentMap(map => ({
        ...map,
        [suffix]: deployments
      }))
    } else {
      deployments = deploymentMap[suffix]
    }
    const message = (deployments.map((deployment, i) => {
      const httpPort = _.get(deployment, 'ContainerPort');
      return <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '5px' }}>
        {httpPort > 0 ? <>
          node{i + 1}:
          <a target='_blank'
            href={getLinkByDeployment(deployment)}
            style={{ marginLeft: '5px', color: '#18c99d' }}
            onClick={(e) => e.stopPropagation()} >
            Open Link
          </a>
        </> : `node${i + 1}: HTTP Endpoint is currently unavailable.`}
      </div>
    })
    );
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Nodes HTTP Endpoints",
      message: message
    }));
  }

  const navToDetail = (isStopped, baseId, suffix, shard) => {
    if (isStopped) {
      history.push(mergeUrl(Urls.AI_GPU_CLUSTER_DETAIL_BASE, { projectId, id: baseId }));
      return;
    }
    history.push(mergeUrl(Urls.AI_GPU_CLUSTER_DETAIL, { projectId, suffix, shard }));
  }

  const handleClickTitle = (e, type) => {
    e.preventDefault();
    setCurDir(curType === type ? (curDir === 'asc' ? 'desc' : 'asc') : 'desc');
    setCurType(type);
  }

  const getSortValue = (node, type) => {
    switch (type) {
      case 'name':
        return _.get(node, 'NodeAnnotations.nickname');
      case 'id':
        return _.get(node, 'Name');
      case 'region':
        return _.get(node, 'Region') || _.get(node, 'SelectedRegion') || 'N/A'
      case 'status':
        return _.get(node, 'Status');
      case 'size':
        return _.get(node, 'size');
      default:
        return;
    }
  }

  const handleScale = (cluster) => {
    dispatch(UIState.actions.showModal(ModalTypes.SCALE_GPU_CLUSTER, { suffix: cluster.Suffix }));
  }

  return (<div className={'AiServicePage'}>
    <div className={'AiServicePage__header'}>
      <div className={'AiServicePage__header--title'}>
        <div className={'AiServicePage__titleWithIcon'}>
          GPU Cluster
          <Tooltip tooltip={'Open documentation'}>
            <a href="https://docs.thetatoken.org/docs/edgecloud-ai-training-with-gpu-clusters" target='_blank'>
              <QuestionMarkIcon />
            </a>
          </Tooltip>
        </div>
        <ApiKeyNote type={'GPU clusters'} />
      </div>
      <div className={'AiServicePage__header--button'} onClick={handleOpenModel}>
        <div className={'AiServiceHeaderButtonIcon empty'}>
        </div>
        New GPU Cluster
      </div>
    </div>
    <div className={'AiServicePage__content'}>
      {loading &&
        <div className={'EmptyState'}>
          <Loader size={'large'} color={'green'} />
        </div>}
      {!hasNoGpuClusters && !loading && !error && <div className={'AiServicePage__table'}>
        <div className={'AiServicePage__table--header'}>
          <div className={'AiServicePage__table--header-row'}>
            <div className={'AiServicePage__table--cell title clickable'}
              onClick={(e) => handleClickTitle(e, 'name')}>
              Name
              <div className='AiServicePage__filter--sort'>
                {curType === 'name' ?
                  (curDir === 'desc' ? <div className='arrow-down'></div> : <div className='arrow-up'></div>) :
                  <div className='arrow-down-up'></div>}
              </div>
            </div>
            <div className={'AiServicePage__table--cell cluster-id clickable'}
              onClick={(e) => handleClickTitle(e, 'id')}>
              ID
              <div className='AiServicePage__filter--sort'>
                {curType === 'id' ?
                  (curDir === 'desc' ? <div className='arrow-down'></div> : <div className='arrow-up'></div>) :
                  <div className='arrow-down-up'></div>}
              </div>
            </div>
            <div className={'AiServicePage__table--cell view clickable'}
              onClick={(e) => handleClickTitle(e, 'size')}>
              Size
              <div className='AiServicePage__filter--sort'>
                {curType === 'size' ?
                  (curDir === 'desc' ? <div className='arrow-down'></div> : <div className='arrow-up'></div>) :
                  <div className='arrow-down-up'></div>}
              </div>
            </div>
            <div className={'AiServicePage__table--cell view'}>SSH</div>
            <div className={'AiServicePage__table--cell view'}>HTTP Endpoint</div>
            <div className={'AiServicePage__table--cell status clickable'}
              onClick={(e) => handleClickTitle(e, 'status')}>
              Status
              <div className='AiServicePage__filter--sort'>
                {curType === 'status' ?
                  (curDir === 'desc' ? <div className='arrow-down'></div> : <div className='arrow-up'></div>) :
                  <div className='arrow-down-up'></div>}
              </div>
            </div>
            <div className={'AiServicePage__table--cell region clickable'}
              onClick={(e) => handleClickTitle(e, 'region')}>
              Region
              <div className='AiServicePage__filter--sort'>
                {curType === 'region' ?
                  (curDir === 'desc' ? <div className='arrow-down'></div> : <div className='arrow-up'></div>) :
                  <div className='arrow-down-up'></div>}
              </div>
            </div>
            <div className={'AiServicePage__table--cell status'}>
              Machine Type
            </div>
            <div className={'AiServicePage__table--cell ellipsis'}></div>
          </div>
        </div>
        <div className={'AiServicePage__table--body'}>
          {Object.keys(gpuClusters)
            .map((k) => gpuClusters[k])
            .sort((a, b) => {
              let valA = getSortValue(a, curType);
              let valB = getSortValue(b, curType);

              if (valA < valB) return curDir === 'asc' ? -1 : 1;
              if (valA > valB) return curDir === 'asc' ? 1 : -1;
              return 0;
            }).map((gpuCluster, i) => {
              const httpPort = _.get(gpuCluster, 'ContainerPort');
              const status = _.get(gpuCluster, 'Status');
              const isStopped = status === 'failed';
              return (
                <div className={`AiServicePage__table--row clickable`} key={i}
                  onClick={() => navToDetail(isStopped, gpuCluster.ID, gpuCluster.Suffix, gpuCluster.Shard)}>
                  <div className={'AiServicePage__table--cell title'}>
                    <NameSection deployment={gpuCluster} />
                  </div>
                  <div className={'AiServicePage__table--cell cluster-id'}>
                    {gpuCluster.Name}
                  </div>
                  <div className={'AiServicePage__table--cell view'}>
                    {_.get(gpuCluster, 'size')}
                  </div>
                  <div className={'AiServicePage__table--cell view'}>
                    <div className={classNames('AiServicePageViewButton', { active: gpuCluster.Status === 'deployed' })}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (gpuCluster.Status !== 'deployed') return;
                        handleShowSSH(gpuCluster)
                      }}>
                      Show
                    </div>
                  </div>
                  <div className={'AiServicePage__table--cell view'}>
                    <div className={classNames('AiServicePageViewButton', { active: gpuCluster.Status === 'deployed' })}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (gpuCluster.Status !== 'deployed') return;
                        handleShowEndpoint(gpuCluster)
                      }}>
                      Show
                    </div>
                  </div>
                  {/* <div className={classNames('AiServicePage__table--cell view')}>
                    <div className={classNames('AiServicePageViewButton', { active: checkActivePod(gpuCluster) && httpPort > 0 })}>
                      <a href={checkActivePod(gpuCluster) && httpPort > 0 ? getLinkByDeployment(gpuCluster) : ''} target='_blank' onClick={(e) => e.stopPropagation()} >
                        Open
                      </a>
                    </div>
                  </div> */}
                  <div className={`AiServicePage__table--cell status icon`}>{getClusterStatusSum(_.get(gpuCluster, 'Status'), false)}</div>
                  <div className={'AiServicePage__table--cell region'}>{_.get(gpuCluster, 'Region') || _.get(gpuCluster, 'SelectedRegion') || 'N/A'}</div>
                  <div className={'AiServicePage__table--cell status'}>
                    <Tooltip tooltip={<div className={"AiServicePage__tooltip"}>
                      {vms[gpuCluster.NodeMachineType] ? getVmResourcesString(vms[gpuCluster.NodeMachineType].resources) : 'N/A'}
                    </div>}>{vms[gpuCluster.NodeMachineType] ? vms[gpuCluster.NodeMachineType].name : 'N/A'}</Tooltip>
                  </div>
                  <div className={'AiServicePage__table--cell ellipsis'}>
                    <div className={'AiServicePageEllipsis'} onClick={(e) => handlePopup(e, i)}>
                      <EllipsisIcon />
                    </div>
                    {activePopup === i && <div className={'AiServicePageEllipsis__popup'} onClick={(e) => { e.stopPropagation() }}>
                      <div className={'AiServicePageEllipsis__popup--row'} onClick={() => handleScale(gpuCluster)}>Scale</div>
                      {/* {isStopped && <div className={'AiServicePageEllipsis__popup--row'} onClick={() => handleStart(gpuCluster)}>Start</div>}
                      {!isStopped && <div className={'AiServicePageEllipsis__popup--row'} onClick={() => handleStop(gpuCluster)}>Stop</div>} */}
                      <div className={'AiServicePageEllipsis__popup--row'} onClick={() => handleDelete(gpuCluster)}>Delete</div>
                      {/* <div className={`AiServicePageEllipsis__popup--row ${isStopped ? '' : 'disabled'}`} onClick={() => handleReconfigure(gpuCluster, isStopped)}>Reconfigure</div> */}
                    </div>}
                  </div>
                </div>
              )
            })}
        </div>
      </div>}
      {hasNoGpuClusters && !loading && !error &&
        <div className={'EmptyState'}>
          <div className={'EmptyState__title'}>No GPU Cluster</div>
          <div className={'EmptyState__subtitle'}>Create your first GPU Cluster with SSH access to get started</div>
          <Button size={'large'}
            title={'New GPU Cluster'}
            onClick={handleOpenModel}
          />
        </div>}
      {!loading && error && <div className={"AiServicePage__error"}>
        {error}
        <Button onClick={async () => {
          await fetchData();
          setLoading(false);
        }}
          color={"green-outline"}
          title={"Retry"} />
      </div>}
    </div>
  </div >
  );
}

const NameSection = ({ deployment }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const nameRef = useRef();
  const status = getStatus(deployment.PodPhase);
  const isStopped = status === 'stopped';
  const handleEdit = () => {
    setIsEditing(true);
  }
  const handleSubmit = async () => {
    let nickname = nameRef.current.value;
    let projectId = deployment.ProjectID;
    if (isStopped) {
      await dispatch(Ai.actions.updateDeploymentBaseDetail(deployment.ID, {
        project_id: projectId,
        name: _.get(deployment, 'Name'),
        annotations: {
          ..._.get(deployment, 'NodeAnnotations'),
          nickname
        },
        vm_id: _.get(deployment, 'NodeMachineType'),
        volumes: JSON.stringify(_.get(deployment, 'Volumes')),
        selected_region: _.get(deployment, 'SelectedRegion')
      }))
    } else {
      await dispatch(Ai.actions.updateDeployment(deployment.Shard, deployment.Suffix, {
        project_id: projectId,
        annotations: {
          nickname: nickname,
          tags: _.get(deployment, 'NodeAnnotations.tags')
        }
      }))
    }
    await dispatch(Ai.actions.fetchGpuNodes(projectId));
    setIsEditing(false);
  }
  const handleCancel = () => {
    setIsEditing(false);
  }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }

  return <div onClick={e => e.stopPropagation()} className='AiServicePage__name-warp'>
    {!isEditing ? _.get(deployment, 'NodeAnnotations.nickname') || `\u00A0` :
      <input type='text' ref={nameRef} defaultValue={_.get(deployment, 'NodeAnnotations.nickname')} onKeyDown={handleKeyDown} />}
    {isEditing ?
      <div className='AiServicePage__icon-warp'>
        <div className='PageTitleBar__right--icon cancel' onClick={handleCancel} title="Cancel"><FaTimesCircle /></div>
        <div className='PageTitleBar__right--icon submit' onClick={handleSubmit} title="Submit"><FaCheckCircle /></div>
      </div> :
      <div className='AiServicePage__icon-warp'>
        <div className='PageTitleBar__right--icon edit' onClick={handleEdit}><FaEdit /></div>
      </div>}
  </div>
}

export default GpuClusterPage;